'use client';
// BottomNavigation - Mobile-only bottom navigation bar
import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  alpha,
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Box,
} from '@mui/material';

import { useAuth, useT } from '@/hooks';
import { BottomNavigationSkeleton } from '../../skeletons';

import { ROUTES } from '@/constants';

interface BottomNavigationProps {
  onMenuClick?: () => void;
  onDepositClick?: () => void;
  isLoading?: boolean;
}

const getValueFromPathname = (path: string): string => {
  if (path === ROUTES.HOME) return 'home';
  if (path === ROUTES.BREAKING) return 'breaking';
  if (path === ROUTES.PORTFOLIO) return 'portfolio';
  if (path === ROUTES.SEARCH) return 'search';
  return '';
};

export const BottomNavigation = ({
  onMenuClick,
  onDepositClick,
  isLoading,
}: BottomNavigationProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const { t } = useT();

  const activeTab = useMemo(() => getValueFromPathname(pathname), [pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    switch (newValue) {
      case 'home':
        router.push(ROUTES.HOME);
        break;
      case 'search':
        router.push(ROUTES.SEARCH);
        break;
      case 'deposit':
        onDepositClick?.();
        break;
      case 'portfolio':
        router.push(ROUTES.PORTFOLIO);
        break;
      case 'more':
        onMenuClick?.();
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return <BottomNavigationSkeleton />;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        display: { xs: 'block', md: 'none' },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? `0 -2px 8px ${alpha(theme.palette.common.black, 0.5)}`
            : `0 -2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      <MuiBottomNavigation
        value={activeTab}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          bgcolor: 'background.paper',
          '& .MuiBottomNavigationAction-root': {
            color: 'text.secondary',
            minWidth: 0,
            padding: '6px 12px',
            '&.Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.6875rem',
              fontWeight: 500,
              mt: 0.5,
              '&.Mui-selected': {
                fontSize: '0.6875rem',
                fontWeight: 600,
              },
            },
          },
        }}
      >
        <BottomNavigationAction
          label={t('nav.home')}
          value="home"
          icon={<HomeIcon sx={{ fontSize: '1.5rem' }} />}
        />
        <BottomNavigationAction
          label={t('nav.search')}
          value="search"
          icon={<SearchIcon sx={{ fontSize: '1.5rem' }} />}
        />
        {isAuthenticated && (
          <BottomNavigationAction
            label={t('nav.deposit')}
            value="deposit"
            icon={<AddCircleIcon sx={{ fontSize: '1.5rem' }} />}
          />
        )}
        {isAuthenticated && (
          <BottomNavigationAction
            label={t('nav.portfolio')}
            value="portfolio"
            icon={<AccountBalanceWalletIcon sx={{ fontSize: '1.5rem' }} />}
          />
        )}
        {!isAuthenticated && (
          <BottomNavigationAction
            label={t('nav.more')}
            value="more"
            icon={<MenuIcon sx={{ fontSize: '1.5rem' }} />}
          />
        )}
      </MuiBottomNavigation>
    </Box>
  );
};

export default BottomNavigation;
